import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBook, faSearch, faDownload, faExternalLinkAlt, 
    faCode, faVideo, faFileAlt, faList
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';

interface DocumentationItem {
    id: string;
    title: string;
    description: string;
    type: 'guide' | 'api' | 'video' | 'pdf' | 'tutorial';
    category: 'getting-started' | 'telehealth' | 'billing' | 'api' | 'troubleshooting';
    url: string;
    lastUpdated: string;
    tags: string[];
}

const Documentation: React.FC = () => {
    const [docs, setDocs] = useState<DocumentationItem[]>([]);
    const [filteredDocs, setFilteredDocs] = useState<DocumentationItem[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [clinicName, setClinicName] = useState<string>('Facility');

    useEffect(() => {
        document.title = "Documentation - Uzima Healthcare";
        loadClinicData();
        loadDocumentation();
    }, []);

    useEffect(() => {
        filterDocs();
    }, [docs, searchQuery, categoryFilter, typeFilter]);

    const loadClinicData = async () => {
        try {
            const storedClinicName = await localForage.getItem('clinic_name');
            if (storedClinicName) {
                setClinicName(storedClinicName as string);
            }
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    };

    const loadDocumentation = () => {
        // Mock documentation data - in real app, fetch from API
        const mockDocs: DocumentationItem[] = [
            {
                id: 'DOC-001',
                title: 'Getting Started Guide',
                description: 'Complete guide to setting up and using the Uzima platform for the first time.',
                type: 'guide',
                category: 'getting-started',
                url: '/docs/getting-started',
                lastUpdated: '2025-09-05',
                tags: ['setup', 'basics', 'onboarding']
            },
            {
                id: 'DOC-002',
                title: 'Telehealth API Reference',
                description: 'Complete API documentation for telehealth session management and participant handling.',
                type: 'api',
                category: 'api',
                url: '/docs/api/telehealth',
                lastUpdated: '2025-09-04',
                tags: ['api', 'telehealth', 'sessions', 'participants']
            },
            {
                id: 'DOC-003',
                title: 'Session Recording Tutorial',
                description: 'Step-by-step video tutorial on how to record and manage telehealth sessions.',
                type: 'video',
                category: 'telehealth',
                url: '/docs/videos/session-recording',
                lastUpdated: '2025-09-03',
                tags: ['recording', 'tutorial', 'video']
            },
            {
                id: 'DOC-004',
                title: 'Billing System Guide',
                description: 'Comprehensive guide to managing billing, payments, and financial transactions.',
                type: 'guide',
                category: 'billing',
                url: '/docs/billing-guide.pdf',
                lastUpdated: '2025-09-02',
                tags: ['billing', 'payments', 'invoicing']
            },
            {
                id: 'DOC-005',
                title: 'User Management API',
                description: 'API endpoints for user creation, authentication, and role management.',
                type: 'api',
                category: 'api',
                url: '/docs/api/users',
                lastUpdated: '2025-09-01',
                tags: ['api', 'users', 'authentication', 'roles']
            },
            {
                id: 'DOC-006',
                title: 'Troubleshooting Common Issues',
                description: 'Solutions to frequently encountered problems and error messages.',
                type: 'guide',
                category: 'troubleshooting',
                url: '/docs/troubleshooting',
                lastUpdated: '2025-08-30',
                tags: ['troubleshooting', 'errors', 'solutions']
            },
            {
                id: 'DOC-007',
                title: 'IoT Device Integration',
                description: 'Guide to connecting and managing IoT devices for health monitoring.',
                type: 'guide',
                category: 'telehealth',
                url: '/docs/iot-integration.pdf',
                lastUpdated: '2025-08-28',
                tags: ['iot', 'devices', 'monitoring', 'integration']
            },
            {
                id: 'DOC-008',
                title: 'Security Best Practices',
                description: 'Security guidelines and best practices for protecting patient data.',
                type: 'pdf',
                category: 'getting-started',
                url: '/docs/security-best-practices.pdf',
                lastUpdated: '2025-08-25',
                tags: ['security', 'privacy', 'compliance']
            }
        ];
        setDocs(mockDocs);
    };

    const filterDocs = () => {
        let filtered = docs;

        // Filter by search query
        if (searchQuery) {
            filtered = filtered.filter(doc => 
                doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                doc.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
            );
        }

        // Filter by category
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(doc => doc.category === categoryFilter);
        }

        // Filter by type
        if (typeFilter !== 'all') {
            filtered = filtered.filter(doc => doc.type === typeFilter);
        }

        setFilteredDocs(filtered);
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'guide': return faBook;
            case 'api': return faCode;
            case 'video': return faVideo;
            case 'pdf': return faFileAlt;
            case 'tutorial': return faList;
            default: return faBook;
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'guide': return 'primary';
            case 'api': return 'success';
            case 'video': return 'danger';
            case 'pdf': return 'warning';
            case 'tutorial': return 'info';
            default: return 'secondary';
        }
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'getting-started': return 'primary';
            case 'telehealth': return 'success';
            case 'billing': return 'warning';
            case 'api': return 'info';
            case 'troubleshooting': return 'danger';
            default: return 'secondary';
        }
    };

    const handleDocClick = (doc: DocumentationItem) => {
        // In a real app, this would open the documentation
        // For now, just show an alert
        alert(`Opening: ${doc.title}\nURL: ${doc.url}`);
    };

    const categories = [
        { id: 'all', name: 'All Categories', count: docs.length },
        { id: 'getting-started', name: 'Getting Started', count: docs.filter(d => d.category === 'getting-started').length },
        { id: 'telehealth', name: 'Telehealth', count: docs.filter(d => d.category === 'telehealth').length },
        { id: 'billing', name: 'Billing', count: docs.filter(d => d.category === 'billing').length },
        { id: 'api', name: 'API', count: docs.filter(d => d.category === 'api').length },
        { id: 'troubleshooting', name: 'Troubleshooting', count: docs.filter(d => d.category === 'troubleshooting').length }
    ];

    const types = [
        { id: 'all', name: 'All Types', count: docs.length },
        { id: 'guide', name: 'Guides', count: docs.filter(d => d.type === 'guide').length },
        { id: 'api', name: 'API Docs', count: docs.filter(d => d.type === 'api').length },
        { id: 'video', name: 'Videos', count: docs.filter(d => d.type === 'video').length },
        { id: 'pdf', name: 'PDFs', count: docs.filter(d => d.type === 'pdf').length },
        { id: 'tutorial', name: 'Tutorials', count: docs.filter(d => d.type === 'tutorial').length }
    ];

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h2 className="fw-bold mb-1">
                                            <FontAwesomeIcon icon={faBook} className="me-2 text-primary" />
                                            Documentation
                                        </h2>
                                        <p className="text-muted mb-0">
                                            Comprehensive guides and resources for {clinicName}
                                        </p>
                                    </div>
                                    <div className="text-end">
                                        <div className="badge bg-primary fs-6 px-3 py-2">
                                            {filteredDocs.length} Documents
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Search and Filters */}
                        <div className="row mb-4">
                            <div className="col-lg-6">
                                <div className="search-box">
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search documentation..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <FontAwesomeIcon 
                                            icon={faSearch} 
                                            className="search-icon position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <select
                                    className="form-select"
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                >
                                    {categories.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.name} ({category.count})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-lg-3">
                                <select
                                    className="form-select"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    {types.map(type => (
                                        <option key={type.id} value={type.id}>
                                            {type.name} ({type.count})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Documentation Grid */}
                        <div className="row">
                            {filteredDocs.map((doc) => (
                                <div key={doc.id} className="col-lg-6 col-xl-4 mb-4">
                                    <div 
                                        className="card border-0 shadow-sm h-100 doc-item-card"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleDocClick(doc)}
                                    >
                                        <div className="card-body">
                                            <div className="d-flex align-items-start">
                                                <div className="flex-shrink-0 me-3">
                                                    <div className={`bg-${getTypeColor(doc.type)} bg-opacity-10 rounded-circle p-3`}>
                                                        <FontAwesomeIcon 
                                                            icon={getTypeIcon(doc.type)} 
                                                            className={`text-${getTypeColor(doc.type)} fs-4`}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                        <h6 className="card-title mb-0">{doc.title}</h6>
                                                        <div className="d-flex gap-1">
                                                            {doc.type === 'pdf' && (
                                                                <FontAwesomeIcon icon={faDownload} className="text-muted" />
                                                            )}
                                                            {doc.type === 'video' && (
                                                                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-muted" />
                                                            )}
                                                        </div>
                                                    </div>
                                                    <p className="card-text text-muted small mb-3">
                                                        {doc.description}
                                                    </p>
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div className="d-flex gap-2">
                                                            <span className={`badge bg-${getCategoryColor(doc.category)} bg-opacity-20 text-${getCategoryColor(doc.category)}`}>
                                                                {doc.category.replace('-', ' ').toUpperCase()}
                                                            </span>
                                                            <span className={`badge bg-${getTypeColor(doc.type)} bg-opacity-20 text-${getTypeColor(doc.type)}`}>
                                                                {doc.type.toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <small className="text-muted">
                                                            Updated {doc.lastUpdated}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* No Results */}
                        {filteredDocs.length === 0 && (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faBook} className="text-muted fs-1 mb-3" />
                                <h5 className="text-muted">No documentation found</h5>
                                <p className="text-muted">
                                    Try adjusting your search terms or filters
                                </p>
                            </div>
                        )}

                        {/* Quick Links */}
                        <div className="row mt-5">
                            <div className="col-12">
                                <div className="card border-0 bg-light">
                                    <div className="card-body text-center py-4">
                                        <h5 className="mb-3">Need More Help?</h5>
                                        <p className="text-muted mb-4">
                                            Can't find what you're looking for? Check out these additional resources.
                                        </p>
                                        <div className="d-flex justify-content-center gap-3">
                                            <button className="btn btn-primary">
                                                <FontAwesomeIcon icon={faVideo} className="me-2" />
                                                Video Tutorials
                                            </button>
                                            <button className="btn btn-outline-primary">
                                                <FontAwesomeIcon icon={faCode} className="me-2" />
                                                API Explorer
                                            </button>
                                            <button className="btn btn-outline-primary">
                                                <FontAwesomeIcon icon={faDownload} className="me-2" />
                                                Download PDFs
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <DashboardFooter />
        </>
    );
};

export default Documentation;
